import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Box, Button, IconButton, Link, Stack, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const exerciseCount = (workout) =>
  (workout.exerciseGroups ?? []).reduce(
    (total, group) => total + (group.exercises?.length ?? 0),
    0
  ) || workout.exercises?.length || 0;

const Header = ({ workout, onPreview, onSave, ...rest }) => {
  const duration = Math.max(1, Math.floor((workout.durationTotal ?? 0) / 60));
  const groups = workout.exerciseGroups?.length ?? 0;
  const savedLabel = workout.updatedAt
    ? `Saved ${moment(workout.updatedAt).fromNow()}`
    : 'Saved just now';

  return (
    <Box {...rest} sx={{ pt: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Link
            component={RouterLink}
            to="/trainer/workouts"
            underline="none"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              color: '#4338ca',
              fontWeight: 700,
              mb: 2,
            }}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            Back to Workouts
          </Link>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#071120' }}>
              {workout.title}
            </Typography>
            <EditOutlinedIcon sx={{ color: '#40536b', fontSize: 21 }} />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <ScheduleIcon sx={{ fontSize: 18, color: '#40536b' }} />
              <Typography sx={{ color: '#40536b', fontWeight: 700 }}>
                {duration} min
              </Typography>
            </Stack>
            <Typography sx={{ color: '#40536b' }}>•</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <FitnessCenterIcon sx={{ fontSize: 18, color: '#40536b' }} />
              <Typography sx={{ color: '#40536b', fontWeight: 700 }}>
                {exerciseCount(workout)} Exercises
              </Typography>
            </Stack>
            <Typography sx={{ color: '#40536b' }}>•</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <ViewModuleIcon sx={{ fontSize: 18, color: '#40536b' }} />
              <Typography sx={{ color: '#40536b', fontWeight: 700 }}>
                {groups} Blocks
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mr: 1 }}>
            <Typography sx={{ color: '#40536b', fontSize: 14 }}>
              {savedLabel}
            </Typography>
            <CheckCircleIcon sx={{ color: '#2dbd70', fontSize: 18 }} />
          </Stack>
          <Button variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={onPreview}>
            Preview
          </Button>
          <Button variant="outlined" startIcon={<ContentCopyIcon />}>
            Duplicate
          </Button>
          <IconButton sx={{ border: '1px solid #d0d9e6', borderRadius: 1 }}>
            <MoreHorizIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={onSave}
            sx={{
              backgroundColor: '#4338ca',
              px: 3,
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            Save Workout
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

Header.propTypes = {
  className: PropTypes.string,
  workout: PropTypes.object,
  onPreview: PropTypes.func,
  onSave: PropTypes.func,
};

export default Header;
